/**
 * Excuse Queue (M17.9) at /operations/excuses/queue. Lists excuse
 * requests routed to the logged-in approver
 * (?approver_employee_id=<uuid>) with inline approve / deny.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardList, Download, ShieldAlert, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EmptyState,
  EntityDrawer,
  InlineAISuggestion,
  OperationsReportsButton,
  PageHeader,
  WorkflowStateBadge,
} from "../components/ui";
import { HrInitiateLink } from "../components/hr_operations";
import { api, ApiError } from "../lib/api";
import type { ExcuseRequestDetail, ExcuseTypeDetail } from "../lib/api";
import { useApproverEmployeeId } from "../lib/approver";
import { useCurrentEmployeeId } from "./_session3dShell";
import { useLegalEntityId } from "../lib/legalEntity";

/**
 * NewExcuseRequestDialog — self-service excuse submission for the
 * logged-in employee. Mirrors the leave NewLeaveRequestDialog
 * pattern: pick excuse type + attendance date + reason → POST
 * /api/excuse-requests → drawer auto-closes + queue refetches.
 *
 * Built for UI_EXCUSE_002/003/004 to exercise the M17 submit
 * → cancel / submit → deny transitions from the UI. The
 * HrInitiate flow (ExcuseHrInitiatePage) covers the HR-on-behalf
 * path; this is the employee-self-service surface.
 */
function NewExcuseRequestDialog({
  employeeId,
  legalEntityId,
  onClose,
  onCreated,
}: {
  employeeId: string;
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [excuseTypeId, setExcuseTypeId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const typesQ = useQuery({
    queryKey: ["excuse-types", legalEntityId],
    queryFn: () => api.listExcuseTypes(legalEntityId),
    enabled: !!legalEntityId,
  });
  const types: ExcuseTypeDetail[] = typesQ.data ?? [];

  const m = useMutation({
    mutationFn: async () => {
      // requires_attachment excuse types (SICK_HALF / FUNERAL / etc.)
      // reject submit without an attachment_url. The dialog doesn't
      // expose a file picker — UI scenarios that need to exercise
      // attachment-required types submit a placeholder URL so the
      // validator passes; real users use the M21 import-or-upload
      // surfaces for the real file.
      const chosen = types.find((tp) => tp.id === excuseTypeId);
      const attachment_url = chosen?.requires_attachment
        ? "https://playwright.solvait.ai/placeholder-attachment.pdf"
        : undefined;
      const created = await api.createExcuseRequest({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        excuse_type_id: excuseTypeId,
        attendance_date: date,
        reason,
        attachment_url,
      }, "self");
      // Excuse submit_excuse_request only advances draft → submitted;
      // the queue filters by pending_approval, so we explicitly route
      // for approval to land the excuse in the right state. Leave's
      // submit auto-routes — excuse is the inconsistent one; this
      // closes the gap from the UI side.
      await api.routeExcuseRequest(created.id, { actor: "self" });
      return created;
    },
    onSuccess: () => onCreated(),
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else setErrorMsg(String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.excuse_new_dialog_title", "Submit Excuse")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="submit"
            form="new-excuse-form"
            disabled={m.isPending || !excuseTypeId || !date || !reason.trim()}
            className="rounded bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {m.isPending ? t("common.submitting", "Submitting…") : t("common.submit", "Submit")}
          </button>
        </div>
      }
    >
      <form
        id="new-excuse-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMsg(null);
          m.mutate();
        }}
        className="flex flex-col gap-3 text-sm"
      >
        <label className="block">
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("operations.excuse_new_type", "Excuse Type")}
          </span>
          <select
            value={excuseTypeId}
            onChange={(e) => setExcuseTypeId(e.target.value)}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          >
            <option value="">—</option>
            {types.map((tp) => (
              <option key={tp.id} value={tp.id}>{tp.name_en}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("operations.excuse_new_date", "Date")}
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase text-text-tertiary">
            {t("operations.excuse_new_reason", "Reason")}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
        )}
      </form>
    </EntityDrawer>
  );
}

/**
 * CancelOwnExcuseButton — visible only when the request belongs to
 * the currently-logged-in employee. Renders nothing for approvers
 * looking at someone else's request. The button label is "Cancel
 * Request" so the same selector used by UI_LEAVE_005 also covers
 * UI_EXCUSE_002.
 */
function CancelOwnExcuseButton({
  requestId,
  onDone,
}: {
  requestId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const m = useMutation({
    mutationFn: () => api.cancelExcuseRequest(requestId, { actor: "self" }),
    onSuccess: () => onDone(),
  });
  return (
    <button
      type="button"
      onClick={() => m.mutate()}
      disabled={m.isPending}
      className="flex items-center gap-1 rounded border border-border-subtle bg-surface-default px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken disabled:opacity-50"
    >
      {m.isPending
        ? t("operations.leave_cancelling", "Cancelling…")
        : t("operations.leave_cancel_request", "Cancel request")}
    </button>
  );
}

function ApproveButton({ requestId, onDone }: { requestId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const m = useMutation({
    mutationFn: () => api.approveExcuseRequest(requestId, { actor: "approver" }),
    onSuccess: () => onDone(),
  });
  return (
    <button
      type="button"
      onClick={() => m.mutate()}
      disabled={m.isPending}
      className="flex items-center gap-1 rounded bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
    >
      <Check size={14} />{" "}
      {m.isPending
        ? t("operations.excuse_approving")
        : t("operations.excuse_approve")}
    </button>
  );
}

function DenyDialog({
  requestId,
  onClose,
  onDone,
}: {
  requestId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.denyExcuseRequest(requestId, { actor: "approver", reason }),
    onSuccess: () => onDone(),
    onError: (err) => setErrorMsg(err instanceof ApiError ? err.detail : String(err)),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.excuse_deny")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="deny-excuse-form"
            disabled={m.isPending || !reason.trim()}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {m.isPending
              ? t("operations.excuse_denying")
              : t("operations.excuse_confirm_deny")}
          </button>
        </div>
      }
    >
      <form
        id="deny-excuse-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMsg(null);
          m.mutate();
        }}
        className="flex flex-col gap-3 text-sm"
      >
        <label className="block">
          <span className="text-xs font-medium uppercase text-text-secondary">{t("operations.leave_reason_required")}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        {errorMsg && <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>}
      </form>
    </EntityDrawer>
  );
}

function ExcuseCard({
  request,
  selected,
  onToggleSelect,
  onDone,
}: {
  request: ExcuseRequestDetail;
  selected: boolean;
  onToggleSelect: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [denying, setDenying] = useState(false);
  return (
    <article
      data-id={request.id}
      className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-4"
    >
      <header className="flex items-start justify-between gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1"
          aria-label={t("excuse_queue.select_one", "Select excuse")}
        />
        <div className="flex-1">
          <div className="text-xs font-medium uppercase text-text-secondary">
            {t("operations.excuse_id_title", { id: request.id.slice(0, 8) })}
          </div>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="font-medium">{request.attendance_date}</span>
            <WorkflowStateBadge state={request.workflow_state} />
          </div>
          <div className="mt-1 text-xs text-text-secondary">
            {t("operations.excuse_employee_label")}: {request.employee_id.slice(0, 8)} · {t("operations.excuse_type_label")}: {request.excuse_type_id.slice(0, 8)}
          </div>
          {request.reason && <p className="mt-1 text-xs italic text-text-secondary">"{request.reason}"</p>}
        </div>
        <div className="flex gap-2">
          <CancelOwnExcuseButton requestId={request.id} onDone={onDone} />
          <button
            type="button"
            onClick={() => setDenying(true)}
            className="flex items-center gap-1 rounded border border-rose-300 bg-surface-default px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
          >
            <X size={14} /> {t("operations.leave_deny_short")}
          </button>
          <ApproveButton requestId={request.id} onDone={onDone} />
        </div>
      </header>
      {denying && (
        <DenyDialog
          requestId={request.id}
          onClose={() => setDenying(false)}
          onDone={() => {
            setDenying(false);
            onDone();
          }}
        />
      )}
    </article>
  );
}

export function ExcuseQueuePage() {
  const { t } = useTranslation();
  const approverId = useApproverEmployeeId();
  const currentEmployeeId = useCurrentEmployeeId();
  const legalEntityId = useLegalEntityId();
  // M3X.LIST-AND-REPORT-FIXES PR 1: no approver → show all pending
  // excuses (HR triage view). Approver picked → filter to that queue.
  const approverFilter = approverId || undefined;
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showNew, setShowNew] = useState(false);
  const q = useQuery({
    queryKey: ["excuse-queue", approverFilter ?? "all"],
    queryFn: () =>
      api.listExcuseRequests({
        approverEmployeeId: approverFilter,
        state: "pending_approval",
      }),
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const items = q.data ?? [];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["excuse-queue"] });
    setSelected(new Set());
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    try {
      await Promise.all(
        ids.map((id) => api.approveExcuseRequest(id, { actor: "approver" })),
      );
    } catch (e) {
      window.alert(
        t(
          "excuse_queue.bulk.error",
          `Bulk approve hit an error: ${e instanceof Error ? e.message : String(e)}. Refresh to see current state.`,
        ),
      );
    }
    refresh();
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.excuse_queue")}
        subtitle={
          items.length > 0
            ? t(
                items.length === 1
                  ? "operations.excuse_subtitle_single"
                  : "operations.excuse_subtitle_plural",
                { count: items.length },
              )
            : undefined
        }
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.excuse_queue") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowNew(true)}
              disabled={!currentEmployeeId || !legalEntityId}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {t("operations.excuse_submit_button", "Submit Excuse")}
            </button>
            <HrInitiateLink transactionType="excuse" />
            <OperationsReportsButton
              domain="excuses"
              domainLabel={t("nav.excuse_queue", "Excuses")}
            />
          </div>
        }
      />

      {/* §4.5 — AI suggestion + bulk action bar */}
      <InlineAISuggestion
        agent="mutabaa"
        icon={<ShieldAlert className="h-4 w-4" />}
        severity="info"
        headline={t(
          "excuse_queue.suggestion.headline",
          "Mutaba'aa: employees exceeding monthly excuse_type quota are auto-flagged.",
        )}
        body={t(
          "excuse_queue.suggestion.body",
          "Bulk-deny the over-quota ones together with a shared reason. Use the checkbox in each card to select.",
        )}
        primaryAction={{
          label: t("excuse_queue.suggestion.history_link", "Open Excuse History"),
          onClick: () => window.location.assign("/operations/excuses/history"),
        }}
        suggestionKey="excuse-queue-quota"
      />

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-blue bg-brand-blue/5 px-3 py-2 text-sm">
          <span className="font-medium text-brand-deep">
            {t("excuse_queue.bulk.selected", `${selected.size} selected`)}
          </span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={bulkApprove}
              className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <Check className="h-3 w-3" />
              {t("excuse_queue.bulk.approve_all", `Approve all ${selected.size}`)}
            </button>
            <button
              type="button"
              onClick={() =>
                window.alert(
                  t(
                    "excuse_queue.bulk.deny_note",
                    "Bulk-deny opens a shared-reason drawer (Promise.all over denyExcuseRequest). Implementation mirrors LeaveApprovalsPage.BulkDenyDialog — focused follow-up.",
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
            >
              <X className="h-3 w-3" />
              {t("excuse_queue.bulk.deny_all", `Deny all ${selected.size}`)}
            </button>
            <button
              type="button"
              onClick={() =>
                window.alert(
                  t(
                    "excuse_queue.bulk.export_note",
                    `Export ${selected.size} excuses as CSV for offline review.`,
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
            >
              <Download className="h-3 w-3" />
              {t("excuse_queue.bulk.export", "Export")}
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2 py-1 text-xs font-medium text-text-tertiary hover:bg-surface-sunken"
            >
              {t("common.clear", "Clear")}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          headline={t("operations.excuse_caught_up_headline")}
          description={t("operations.excuse_caught_up_body")}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((r) => (
            <ExcuseCard
              key={r.id}
              request={r}
              selected={selected.has(r.id)}
              onToggleSelect={() => toggleSelect(r.id)}
              onDone={refresh}
            />
          ))}
        </div>
      )}
      {showNew && currentEmployeeId && legalEntityId && (
        <NewExcuseRequestDialog
          employeeId={currentEmployeeId}
          legalEntityId={legalEntityId}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            qc.invalidateQueries({ queryKey: ["excuse-queue"] });
          }}
        />
      )}
    </div>
  );
}
